import bridge from '@/plugins/vue-constructor-bridge';

import http from '@/services/http';
import loadScript from 'simple-load-script';
import { publicIpv4 } from 'public-ip';
import returnAppConfig from '@/helpers/return-app-config';



const encryption = {
  dependenciesHaveBeenLoaded: false,

  async encryptCardDetails(options) {
    try {
      switch (options.paymentGateway.methods.creditCard.provider) {
        case 'BlueSnap':
          return await encryption.encryptViaBlueSnap(options);
        case 'ShiftFour':
          return await encryption.encryptViaShiftFour(options);
        default:
          return await encryption.encryptViaDefaultMethod(options);
      }
    }

    catch (error) {
      throw error;
    }
  },

  async encryptViaBlueSnap(options) {
    try {
      await encryption.loadDependencies({
        testUrl: 'https://sandbox.bluesnap.com/js/cse/v1.0.4/bluesnap.js',
        productionUrl: 'https://gateway.bluesnap.com/js/cse/v1.0.4/bluesnap.js'
      });

      const wrapper = document.getElementById('bluesnap');
      wrapper.innerHTML = `
        <input type="hidden" data-bluesnap="encryptedCreditCard" value="${options.cardDetails.cardNumber}">
        <input type="hidden" data-bluesnap="encryptedCvv" value="${options.cardDetails.cvv}">
      `;

      const bluesnap = new window.BlueSnap(options.paymentGateway.methods.creditCard.cseKey);
      const formId = 'bluesnap';

      bluesnap.encrypt(formId);

      const cardNumberInputs = document.querySelectorAll(`#${formId} input[type=hidden][name="encryptedCreditCard"]`);
      const cvvInputs = document.querySelectorAll(`#${formId} input[type=hidden][name="encryptedCvv"]`);
      const cardNumber = cardNumberInputs[cardNumberInputs.length - 1].value;
      const cvv = cvvInputs[cvvInputs.length - 1].value;

      return {
        encrypted: {
          cardNumber,
          cvv
        },
        unencrypted: {}
      };
    }

    catch (error) {
      throw error;
    }
  },

  async encryptViaDefaultMethod(options) {
    try {
      return {
        encrypted: {},
        unencrypted: {
          cardNumber: options.cardDetails.cardNumber,
          cvv: options.cardDetails.cvv
        }
      };
    }

    catch (error) {
      throw error;
    }
  },

  async encryptViaShiftFour(options) {
    try {
      /*
        NOTE: A valid test CC:
        4321000000001119
        CVV: 333
        ZIP: 65651
      */

      if (options.isSaving) {
        return {
          encrypted: {},
          unencrypted: {
            cardNumber: options.cardDetails.cardNumber,
            cvv: options.cardDetails.cvv
          }
        };
      }

      const accessBlockResponse = await http.post('/cardfree-api/v1/payment/tokenization-key', {
        data: {
          i4go_clientIP: await publicIpv4()
        },
        storeId: options.paymentGateway.storeId
      });

      const accessBlock = accessBlockResponse.data[0].key;
      const { tokenizationUrl } = accessBlockResponse.data[0];

      const formData = new URLSearchParams();
      formData.append('fuseaction', 'api.jsonPostCardEntry');
      formData.append('i4go_accessblock', accessBlock);
      formData.append('i4go_cardnumber', options.cardDetails.cardNumber);
      formData.append('i4go_expirationmonth', options.cardDetails.cardExpiration.month);
      formData.append('i4go_expirationyear', options.cardDetails.cardExpiration.year);
      formData.append('i4go_cvv2code', options.cardDetails.cvv);

      const paymentTokenizationResponse = await http.post(tokenizationUrl, formData, {
        'Content-Type': 'application/x-www-form-urlencoded'
      });

      const token = paymentTokenizationResponse.data.i4go_uniqueid;

      if (!token) {
        const error = new Error('Failed to receive payment token.');

        bridge.triggerNewRelicEvent('Shift4TokenError', 'Error', 'Shift4 Token Error', { error: error.message });

        throw error;
      }

      return {
        encrypted: {
          token
        },
        unencrypted: {
          cvv: options.cardDetails.cvv
        }
      };
    }

    catch (error) {
      bridge.triggerNewRelicEvent('Shift4EncryptionError', 'Error', 'Shift4 Encryption Error', { error: error.message });

      throw error;
    }
  },

  async loadDependencies(options) {
    try {
      if (!encryption.dependenciesHaveBeenLoaded) {
        const { isProductionEnvironment } = returnAppConfig();
        const scriptUrl = isProductionEnvironment ? options.productionUrl : options.testUrl;

        await loadScript(scriptUrl);

        encryption.dependenciesHaveBeenLoaded = true;
      }
    }

    catch (error) {
      throw error;
    }
  }
};



export default encryption;
